// KES formatting + client-side calculation mirror of backend calc.py

export function kes(v, dp = 2) {
  const n = Number(v || 0);
  return n.toLocaleString("en-KE", { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

export function ltr(v, dp = 2) {
  const n = Number(v || 0);
  return n.toLocaleString("en-KE", { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

const r = (v, n = 3) => {
  const f = Number(v || 0);
  if (Number.isNaN(f)) return 0;
  const p = Math.pow(10, n);
  return Math.round(f * p) / p;
};

// Shifts are stored as AM / PM and named by when they end: 8AM (16:00 the day before to 08:00) and
// 4PM (08:00-16:00); Sunday's one shift "Sun 1PM" runs Saturday 16:00 to Sunday 13:00
export const SHIFT_LABELS = { AM: "8AM", PM: "4PM" };
// a till over by X and another short by about X (within this) looks like a wrong-till payment
export const WRONG_TILL_TOLERANCE = 100;
export const MPESA_CODE_RE = /^[A-Z0-9]{10}$/;
// When a shift starts (Nairobi time). Shifts are named by when they END: the 8AM shift runs from 16:00
// the day before (Monday's from Sunday 13:00); the 4PM shift from 08:00; Sunday's one shift from
// Saturday 16:00 to Sunday 13:00.
export function shiftStartISO(date, shiftType, shiftHours) {
  const hours = { AM: "08:00", PM: "16:00", ...(shiftHours || {}) };
  const day = new Date(`${date}T12:00:00Z`);
  const prev = new Date(day.getTime() - 86400000).toISOString().slice(0, 10);
  if (shiftType === "AM") return `${prev}T${isSunday(prev) ? "13:00" : hours.PM}:00+03:00`;
  if (isSunday(date)) return `${prev}T${hours.PM}:00+03:00`;
  return `${date}T${hours.AM}:00+03:00`;
}

// The price of each product in force when the shift started: the latest version whose "starts from"
// is at or before the shift's start (a price changed mid-shift waits for the next shift)
export function pricesAt(versions, startISO) {
  const start = new Date(startISO).getTime();
  const price = {}, cost = {};
  [...(versions || [])]
    .filter((v) => new Date(v.effective_from || v.created_at).getTime() <= start)
    .sort((a, b) => new Date(b.effective_from || b.created_at) - new Date(a.effective_from || a.created_at))
    .forEach((v) => {
      if (!(v.product_id in price)) price[v.product_id] = v.unit_price;
      if (v.cost_price != null && !(v.product_id in cost)) cost[v.product_id] = v.cost_price;
    });
  return { price, cost };
}

export const mpesaCode = (v) => String(v || "").replace(/\s+/g, "").toUpperCase();

// Pull M-Pesa code + amount pairs out of pasted text: confirmation SMSes ("TJB4ABCDEF Confirmed.
// Ksh1,000.00 received from ...") or simple "CODE amount" lines from a statement / notes.
export function parseMpesaText(text) {
  const out = [];
  const seen = new Set();
  const add = (code, amount) => {
    const c = mpesaCode(code);
    const a = Number(String(amount).replace(/,/g, ""));
    if (!MPESA_CODE_RE.test(c) || !(a > 0) || seen.has(c)) return;
    seen.add(c);
    out.push({ code: c, amount: a });
  };
  const sms = /\b([A-Z0-9]{10})\s+Confirmed\b[\s\S]*?Ksh\s?([\d,]+(?:\.\d{1,2})?)/gi;
  let m;
  while ((m = sms.exec(text))) add(m[1], m[2]);
  const lines = /^\s*([A-Z0-9]{10})[\s,;:|\t]+(?:Ksh\.?\s*)?([\d,]+(?:\.\d{1,2})?)\s*$/gim;
  while ((m = lines.exec(text))) add(m[1], m[2]);
  return out;
}
// Sundays have one shift only (stored PM), 1PM to Monday 8AM; Saturday's 4PM shift runs until 1PM Sunday.
export const isSunday = (date) => !!date && new Date(`${String(date).slice(0, 10)}T12:00:00`).getDay() === 0;
export const shiftLabel = (t, date) => (t === "PM" && isSunday(date) ? "Sun 1PM" : SHIFT_LABELS[t] || t);

export const STATUS_LABELS = {
  draft: "Draft",
  submitted: "Submitted",
  manager_reviewed: "Manager Reviewed",
  accountant_approved: "Accountant Approved",
  locked: "Locked",
};

// shift: arrays (fuel_readings, tank_dips, nonfuel, payments, expenses, fuel_prices, actual_cash)
// config: {station, pumps, tanks, products, nonfuel_items, payment_methods}
export function computeShift(shift, config) {
  const station = config.station || {};
  const cashTol = Number(station.cash_tolerance ?? 100);
  const warnPct = Number(station.throughput_warn_pct ?? 0.2);
  const critPct = Number(station.throughput_crit_pct ?? 0.5);

  const pumps = Object.fromEntries((config.pumps || []).map((p) => [String(p.id), p]));
  const tanks = Object.fromEntries((config.tanks || []).map((t) => [String(t.id), t]));
  const items = Object.fromEntries((config.nonfuel_items || []).map((i) => [String(i.id), i]));
  const methods = Object.fromEntries((config.payment_methods || []).map((m) => [String(m.id), m]));
  const fuelPrices = shift.fuel_prices || {};
  const fuelCostPrices = shift.fuel_cost_prices || {};

  // Manual = physical meter reading; Electronic = pump's own totalizers (liters + cumulative KES) —
  // Electronic is authoritative for sales/throughput, matching the station's reconciliation sheet.
  const fuel_lines = [];
  let fuel_sales_total = 0;
  let fuel_cost_total = 0;
  const liters_by_tank = {};
  const test_by_tank = {};
  let rtt_liters_total = 0;
  let rtt_value_total = 0;
  (shift.fuel_readings || []).forEach((row) => {
    const pump = pumps[String(row.pump_id)] || {};
    const product_id = String(row.product_id || pump.product_id || "");
    const opening = r(row.opening);
    const closing = r(row.closing);
    const testing = r(row.testing);
    const price = r(fuelPrices[product_id] || 0, 4);
    const cost_price = r(fuelCostPrices[product_id] || 0, 4);

    const manual_liters_sold = r(closing - opening - testing);
    const manual_amount = r(manual_liters_sold * price, 2);

    const elec_opening_l = r(row.elec_opening_l);
    const elec_closing_l = r(row.elec_closing_l);
    const elec_opening_kes = r(row.elec_opening_kes, 2);
    const elec_closing_kes = r(row.elec_closing_kes, 2);
    const electronic_liters_sold = r(elec_closing_l - elec_opening_l - testing);
    // No KES sales register on the pump (both KES readings zero) → electronic liters × price
    const has_elec_kes = elec_opening_kes !== 0 || elec_closing_kes !== 0;
    const electronic_amount = has_elec_kes ? r(elec_closing_kes - elec_opening_kes, 2) : r(electronic_liters_sold * price, 2);

    const has_electronic = elec_opening_l !== 0 || elec_closing_l !== 0;
    const liters_sold = has_electronic ? electronic_liters_sold : manual_liters_sold;
    const amount = has_electronic ? electronic_amount : manual_amount;
    const cost = r(liters_sold * cost_price, 2);
    const gross_profit = r(amount - cost, 2);

    fuel_sales_total += amount;
    fuel_cost_total += cost;
    // "testing" = RTT (returned to tank) — not a sale, already left out of liters_sold above
    rtt_liters_total = r(rtt_liters_total + testing, 2);
    rtt_value_total = r(rtt_value_total + testing * price, 2);
    const tank_id = String(pump.tank_id || "");
    if (tank_id) {
      liters_by_tank[tank_id] = r((liters_by_tank[tank_id] || 0) + liters_sold);
      test_by_tank[tank_id] = r((test_by_tank[tank_id] || 0) + testing, 2);
    }
    fuel_lines.push({
      pump_id: String(row.pump_id), product_id, opening, closing, testing,
      manual_liters_sold, manual_amount, elec_opening_l, elec_closing_l, elec_opening_kes, elec_closing_kes,
      electronic_liters_sold, electronic_amount,
      variance_liters: r(electronic_liters_sold - manual_liters_sold, 3),
      variance_amount: r(electronic_amount - manual_amount, 2),
      liters_sold, unit_price: price, amount, cost_price, cost, gross_profit,
    });
  });

  // ----- Deliveries (itemized: invoice details + offloaded vs invoiced volume) — see calc.py -----
  const tank_delivery_lines = [];
  const deliveriesByTank = {};
  const tanksWithDeliveries = new Set();
  (shift.tank_deliveries || []).forEach((row) => {
    const tank_id = String(row.tank_id);
    tanksWithDeliveries.add(tank_id);
    const invoice_volume = r(row.invoice_volume, 2);
    const offloaded_quantity = r(row.offloaded_quantity, 2);
    const variance = r(offloaded_quantity - invoice_volume, 2);
    const pct = invoice_volume ? r((Math.abs(variance) / invoice_volume) * 100, 3) : 0;
    const status = pct <= warnPct ? "within" : (pct <= critPct ? "warning" : "critical");
    deliveriesByTank[tank_id] = r((deliveriesByTank[tank_id] || 0) + offloaded_quantity, 2);
    tank_delivery_lines.push({
      tank_id, invoice_no: row.invoice_no || "", date: row.date || "",
      product_id: String(row.product_id || tanks[tank_id]?.product_id || ""),
      invoice_amount: r(row.invoice_amount, 2), invoice_volume, offloaded_quantity,
      variance, variance_pct: pct, status,
    });
  });

  const pctStatus = (pct) => (pct <= warnPct ? "within" : (pct <= critPct ? "warning" : "critical"));
  const tank_lines = [];
  let total_tank_variance_l = 0;
  (shift.tank_dips || []).forEach((row) => {
    const tank = tanks[String(row.tank_id)] || {};
    const opening_dip = r(row.opening_dip);
    const deliveries = tanksWithDeliveries.has(String(row.tank_id)) ? r(deliveriesByTank[String(row.tank_id)], 2) : r(row.deliveries);
    const rtt = r(row.rtt);
    const closing_dip = r(row.closing_dip);
    const dip_sales = r(opening_dip + deliveries + rtt - closing_dip);
    const pump_throughput = r(liters_by_tank[String(row.tank_id)] || 0);
    const pump_test = r(test_by_tank[String(row.tank_id)] || 0, 2);
    const variance_l = r(pump_throughput - dip_sales);
    const pct = pump_throughput ? r((Math.abs(variance_l) / pump_throughput) * 100, 3) : 0;
    const status = pctStatus(pct);
    total_tank_variance_l += variance_l;

    // Second, optional dip set from the TLS gauge — reconciled against the same pump throughput
    const tls_opening_dip = r(row.tls_opening_dip);
    const tls_closing_dip = r(row.tls_closing_dip);
    const has_tls = tls_opening_dip !== 0 && tls_closing_dip !== 0; // closing 0 = gauge not read yet (see calc.py)
    let tls_dip_sales = null, tls_variance_l = null, tls_variance_pct = null, tls_status = null;
    if (has_tls) {
      tls_dip_sales = r(tls_opening_dip + deliveries + rtt - tls_closing_dip);
      tls_variance_l = r(pump_throughput - tls_dip_sales);
      tls_variance_pct = pump_throughput ? r((Math.abs(tls_variance_l) / pump_throughput) * 100, 3) : 0;
      tls_status = pctStatus(tls_variance_pct);
    }
    tank_lines.push({
      tank_id: String(row.tank_id), product_id: String(tank.product_id || ""), opening_dip, deliveries, rtt, pump_test, closing_dip, dip_sales, pump_throughput, variance_l, variance_pct: pct, status,
      tls_opening_dip, tls_closing_dip, tls_dip_sales, tls_variance_l, tls_variance_pct, tls_status,
    });
  });

  // ----- Non-fuel deliveries (itemized: invoice details + received vs invoiced quantity) — see calc.py -----
  const nonfuel_delivery_lines = [];
  const receivedByItem = {};
  const itemsWithDeliveries = new Set();
  (shift.nonfuel_deliveries || []).forEach((row) => {
    const item_id = String(row.item_id);
    itemsWithDeliveries.add(item_id);
    const invoice_quantity = r(row.invoice_quantity, 2);
    const actual_quantity = r(row.actual_quantity, 2);
    const variance = r(actual_quantity - invoice_quantity, 2);
    const pct = invoice_quantity ? r((Math.abs(variance) / invoice_quantity) * 100, 3) : 0;
    const status = pct <= warnPct ? "within" : (pct <= critPct ? "warning" : "critical");
    receivedByItem[item_id] = r((receivedByItem[item_id] || 0) + actual_quantity, 2);
    nonfuel_delivery_lines.push({
      item_id, invoice_no: row.invoice_no || "", date: row.date || "",
      invoice_amount: r(row.invoice_amount, 2), invoice_quantity, actual_quantity,
      variance, variance_pct: pct, status,
    });
  });

  const groupFor = (category) => (category === "lube" ? "lube" : "lpg_accessory");
  const nonfuel_lines = [];
  let nonfuel_revenue_total = 0;
  let nonfuel_cost_total = 0;
  const nonfuel_revenue_by_group = { lube: 0, lpg_accessory: 0 };
  (shift.nonfuel || []).forEach((row) => {
    const item = items[String(row.item_id)] || {};
    const item_id = String(row.item_id);
    const opening = r(row.opening);
    const received = itemsWithDeliveries.has(item_id) ? r(receivedByItem[item_id], 2) : r(row.received);
    const returned = r(row.returned); // LPG cylinders returned into stock
    const closing = r(row.closing);
    // shop-front sites count both places; the sale is off the combined stock
    const shop_opening = r(row.shop_opening);
    const shop_closing = r(row.shop_closing);
    const sold = r(opening + shop_opening + received + returned - closing - shop_closing);
    const unit_price = r(row.unit_price ?? item.unit_price ?? 0, 2);
    const cost_price = r(row.cost_price ?? item.cost_price ?? 0, 2);
    const revenue = r(sold * unit_price, 2);
    const cost = r(sold * cost_price, 2);
    nonfuel_revenue_total += revenue;
    nonfuel_cost_total += cost;
    const group = groupFor(item.category);
    nonfuel_revenue_by_group[group] = r(nonfuel_revenue_by_group[group] + revenue, 2);
    nonfuel_lines.push({ item_id: String(row.item_id), opening, received, returned, closing, shop_opening, shop_closing, sold, unit_price, cost_price, revenue, cost, gross_profit: r(revenue - cost, 2), category: item.category });
  });

  // ----- Lubes & LPG collections: any number of people, each row = who / for / method / amount
  // (older shifts named one person per group instead — still applied; see calc.py) -----
  const nonfuelGroupAttendantMap = {};
  Object.entries(shift.nonfuel_group_attendants || {}).forEach(([k, v]) => { if (v) nonfuelGroupAttendantMap[k] = String(v); });
  const nonfuel_payment_lines = [];
  const nonfuelDeclaredByGroup = { lube: 0, lpg_accessory: 0 };
  const nonfuelDeclarers = { lube: [], lpg_accessory: [] };
  (shift.nonfuel_payments || []).forEach((row) => {
    if (row.group !== "lube" && row.group !== "lpg_accessory") return;
    const method = methods[String(row.method_id)] || {};
    const amount = r(row.amount, 2);
    const kind = method.kind || "other";
    const method_id = String(row.method_id);
    const attendant_id = (row.attendant_id ? String(row.attendant_id) : "") || nonfuelGroupAttendantMap[row.group] || null;
    nonfuelDeclaredByGroup[row.group] = r(nonfuelDeclaredByGroup[row.group] + amount, 2);
    if (amount && attendant_id && !nonfuelDeclarers[row.group].includes(attendant_id)) nonfuelDeclarers[row.group].push(attendant_id);
    nonfuel_payment_lines.push({
      group: row.group, attendant_id,
      method_id, amount, kind, reference: row.reference || "", till_id: row.till_id || null,
    });
  });

  // Store sales (Kariokor: filters & air cleaners sold straight from the store - see calc.py)
  const store_sale_lines = [];
  (shift.store_sales || []).forEach((row) => {
    const item = items[String(row.item_id)] || {};
    const qty = r(row.qty, 3);
    if (!qty) return;
    const unit_price = r(row.unit_price ?? item.unit_price ?? 0, 2);
    const cost_price = r(row.cost_price ?? item.cost_price ?? 0, 2);
    const method = methods[String(row.method_id)] || {};
    const amount = r(qty * unit_price, 2);
    const line = { item_id: String(row.item_id), qty, unit_price, cost_price, amount, cost: r(qty * cost_price, 2),
      method_id: String(row.method_id), kind: method.kind || "other", attendant_id: row.attendant_id || null,
      till_id: row.till_id || null, reference: row.reference || "" };
    store_sale_lines.push(line);
    nonfuel_revenue_total += amount;
    nonfuel_cost_total += line.cost;
    nonfuel_lines.push({ item_id: line.item_id, opening: 0, received: 0, returned: 0, closing: 0, shop_opening: 0, shop_closing: 0, sold: qty,
      unit_price, cost_price, revenue: amount, cost: line.cost, gross_profit: r(amount - line.cost, 2), category: item.category || "spare", from_store: true });
    nonfuel_payment_lines.push({ group: "store", attendant_id: line.attendant_id, method_id: line.method_id, amount, kind: line.kind,
      reference: line.reference, till_id: line.till_id });
  });
  // spares are sold by hand, never through Vivo - left out of the Vivo check
  const store_sales_total = r(store_sale_lines.reduce((a, l) => a + l.amount, 0), 2);

  const payment_lines = [];
  let payments_total = 0;
  let non_cash_total = 0;
  let cash_declared = 0;
  const payments_by_method = {};
  (shift.payments || []).forEach((row) => {
    const method = methods[String(row.method_id)] || {};
    const amount = r(row.amount, 2);
    const kind = method.kind || "other";
    const attendant_id = row.attendant_id || null;
    payments_total += amount;
    if (kind === "cash") cash_declared += amount;
    else non_cash_total += amount;
    const method_id = String(row.method_id);
    payments_by_method[method_id] = r((payments_by_method[method_id] || 0) + amount, 2);
    payment_lines.push({ attendant_id, method_id, amount, kind, reference: row.reference || "" });
  });

  // Non-fuel payments feed into the SAME shift-wide cash/non-cash totals as fuel payments.
  nonfuel_payment_lines.forEach((line) => {
    payments_total = r(payments_total + line.amount, 2);
    if (line.kind === "cash") cash_declared = r(cash_declared + line.amount, 2);
    else non_cash_total = r(non_cash_total + line.amount, 2);
    payments_by_method[line.method_id] = r((payments_by_method[line.method_id] || 0) + line.amount, 2);
  });

  // Lube cash collected by forecourt attendants — counted as cash on both sides of their
  // reconciliation and taken off what the Lubes declaration still has to cover (see calc.py)
  const attendantLubeSales = {};
  Object.entries(shift.attendant_lube_sales || {}).forEach(([k, v]) => { if (r(v, 2)) attendantLubeSales[String(k)] = r(v, 2); });
  const attendant_lube_total = r(Object.values(attendantLubeSales).reduce((a, v) => a + v, 0), 2);
  payments_total = r(payments_total + attendant_lube_total, 2);
  cash_declared = r(cash_declared + attendant_lube_total, 2);

  // Attendant LUBES money covers LPG too: set against lube revenue first, the rest against LPG
  let attendantNonfuelLeft = attendant_lube_total;
  const nonfuel_group_lines = ["lube", "lpg_accessory"].map((group) => {
    const declarer_ids = nonfuelDeclarers[group].length ? nonfuelDeclarers[group] : (nonfuelGroupAttendantMap[group] ? [nonfuelGroupAttendantMap[group]] : []);
    const attendant_id = declarer_ids.length === 1 ? declarer_ids[0] : "";
    let expected_revenue = r(nonfuel_revenue_by_group[group] || 0, 2);
    const covered = group === "lube" ? Math.min(Math.max(expected_revenue, 0), attendantNonfuelLeft) : attendantNonfuelLeft;
    attendantNonfuelLeft = r(attendantNonfuelLeft - covered, 2);
    expected_revenue = r(expected_revenue - covered, 2);
    const declared_amount = r(nonfuelDeclaredByGroup[group] || 0, 2);
    const variance = r(declared_amount - expected_revenue, 2);
    const status = Math.abs(variance) <= cashTol ? "within" : (Math.abs(variance) <= cashTol * 5 ? "warning" : "critical");
    return {
      group, attendant_id: attendant_id || null, declarer_ids,
      sold_revenue: r(nonfuel_revenue_by_group[group] || 0, 2), attendant_collected: r(covered, 2),
      expected_revenue, declared_amount, variance, status,
    };
  });

  // ----- Per-attendant reconciliation — expected = Vivo-reported fuel sales (attendant_expected),
  // or, when the automation is down, the meter sales of nozzles assigned to them (see calc.py) -----
  const pumpAttendantMap = {};
  Object.entries(shift.pump_attendants || {}).forEach(([k, v]) => { if (v) pumpAttendantMap[String(k)] = String(v); });
  const attendantSales = {};
  fuel_lines.forEach((line) => {
    const att = pumpAttendantMap[line.pump_id] || "";
    if (att || line.amount) attendantSales[att] = r((attendantSales[att] || 0) + line.amount, 2);
  });
  // Vivo's figure per attendant; on vivo_includes_nonfuel shifts it includes their lubes & LPG, so the
  // fuel part is that less the lube/LPG money they collected (see calc.py)
  const vivoIncludesNonfuel = !!shift.vivo_includes_nonfuel;
  const vivoReported = {};
  Object.entries(shift.attendant_expected || {}).forEach(([k, v]) => {
    if (v !== null && v !== undefined && v !== "") vivoReported[String(k)] = r(v, 2);
  });
  const attendantNonfuel = {};
  nonfuel_payment_lines.forEach((line) => {
    if (line.attendant_id && line.amount && line.group !== "store") attendantNonfuel[line.attendant_id] = r((attendantNonfuel[line.attendant_id] || 0) + line.amount, 2);
  });
  const systemExpected = {};
  Object.entries(vivoReported).forEach(([k, v]) => {
    systemExpected[k] = vivoIncludesNonfuel ? r(v - (attendantNonfuel[k] || 0), 2) : v;
  });
  const hasSystem = Object.keys(systemExpected).length > 0;
  if (hasSystem) delete attendantSales[""];
  const attendantPayments = {};
  const attendantCollected = {};
  const attendantCash = {};
  const attendantNonCash = {};
  payment_lines.forEach((line) => {
    const att = line.attendant_id || "";
    attendantPayments[att] = attendantPayments[att] || {};
    attendantPayments[att][line.method_id] = r((attendantPayments[att][line.method_id] || 0) + line.amount, 2);
    attendantCollected[att] = r((attendantCollected[att] || 0) + line.amount, 2);
    if (line.kind === "cash") attendantCash[att] = r((attendantCash[att] || 0) + line.amount, 2);
    else attendantNonCash[att] = r((attendantNonCash[att] || 0) + line.amount, 2);
  });
  // attendant_expected keys include attendants added to the shift with no Vivo figure yet ("")
  const attendantIds = new Set([...Object.keys(attendantSales), ...Object.keys(attendantCollected), ...Object.keys(shift.attendant_expected || {}), ...Object.keys(attendantLubeSales)]);
  const attendant_lines = [...attendantIds].map((att_id) => {
    const pump_fuel_sales = r(attendantSales[att_id] || 0, 2);
    const fromSystem = att_id in systemExpected;
    const fuel_expected = fromSystem ? systemExpected[att_id] : pump_fuel_sales;
    const lube_sales = attendantLubeSales[att_id] || 0;
    const expected_total = r(fuel_expected + lube_sales, 2);
    const collected = r((attendantCollected[att_id] || 0) + lube_sales, 2);
    const variance = r(collected - expected_total, 2);
    const status = Math.abs(variance) <= cashTol ? "within" : (Math.abs(variance) <= cashTol * 5 ? "warning" : "critical");
    return {
      attendant_id: att_id || null,
      assigned_fuel_sales: fuel_expected,
      expected_source: fromSystem ? "system" : "pumps",
      vivo_reported: fromSystem ? vivoReported[att_id] : null,
      nonfuel_collected: attendantNonfuel[att_id] || 0,
      pump_fuel_sales, lube_sales, expected_total,
      payments_by_method: attendantPayments[att_id] || {},
      cash_collected: r((attendantCash[att_id] || 0) + lube_sales, 2),
      non_cash_collected: r(attendantNonCash[att_id] || 0, 2),
      collected_total: collected,
      variance, status,
    };
  }).sort((a, b) => (a.attendant_id === null) - (b.attendant_id === null) || String(a.attendant_id).localeCompare(String(b.attendant_id)));

  // ----- Itemized transactions (optional detail to match against each payment lump sum) -----
  const lumpByGroup = {};
  payment_lines.forEach((line) => {
    const key = `${line.attendant_id || ""}|${line.method_id}`;
    lumpByGroup[key] = r((lumpByGroup[key] || 0) + line.amount, 2);
  });
  const txnByGroup = {};
  (shift.payment_transactions || []).forEach((t) => {
    const key = `${t.attendant_id || ""}|${t.method_id}`;
    (txnByGroup[key] = txnByGroup[key] || []).push({ amount: r(t.amount, 2), reference: t.reference || "", note: t.note || "" });
  });
  const groupKeys = new Set([...Object.keys(lumpByGroup), ...Object.keys(txnByGroup)]);
  const payment_itemization = [...groupKeys].map((key) => {
    const [attendant_id, method_id] = key.split("|");
    const txns = txnByGroup[key] || [];
    const itemized_total = r(txns.reduce((a, t) => a + t.amount, 0), 2);
    const lump_amount = r(lumpByGroup[key] || 0, 2);
    return { attendant_id: attendant_id || null, method_id, lump_amount, itemized_total, variance: r(itemized_total - lump_amount, 2), transactions: txns };
  }).sort((a, b) => (a.attendant_id === null) - (b.attendant_id === null) || String(a.attendant_id).localeCompare(String(b.attendant_id)) || a.method_id.localeCompare(b.method_id));

  // ----- M-Pesa till reconciliation (see calc.py for the full explanation) -----
  const tills = Object.fromEntries((config.tills || []).map((t) => [String(t.id), t]));
  // A till can be shared by several attendants — values are lists (a plain string from older
  // shifts counts as one attendant). attendant_id is only set when exactly one is assigned.
  const tillAttendantMap = {};
  Object.entries(shift.till_attendants || {}).forEach(([k, v]) => {
    tillAttendantMap[String(k)] = (Array.isArray(v) ? v : (v ? [v] : [])).filter(Boolean).map(String);
  });
  const attendantMpesa = {};
  payment_lines.forEach((line) => {
    if (line.kind === "mobile_money") {
      const att = line.attendant_id || "";
      attendantMpesa[att] = r((attendantMpesa[att] || 0) + line.amount, 2);
    }
  });
  const tillMpesaDirect = {};
  // Lube/LPG M-Pesa: to the till chosen on the row, else to its collector's till(s)
  nonfuel_payment_lines.forEach((line) => {
    if (line.kind !== "mobile_money" || !line.amount) return;
    if (line.till_id) tillMpesaDirect[line.till_id] = r((tillMpesaDirect[line.till_id] || 0) + line.amount, 2);
    else if (line.attendant_id) attendantMpesa[line.attendant_id] = r((attendantMpesa[line.attendant_id] || 0) + line.amount, 2);
  });
  // Wrong-till payments (see calc.py): moved from the till they were meant for to the one they landed in
  // Account payments received: customers settling their accounts (see calc.py) — M-Pesa into a
  // till's expected balance, cash added to the cash to bank; never sales
  const account_receipt_lines = [];
  const accountTillIn = {};
  let account_cash_received = 0;
  const account_receipts_by_method = {};
  (shift.account_receipts || []).forEach((a) => {
    const amount = r(a.amount, 2);
    if (!amount) return;
    const method_id = String(a.method_id || "");
    const kind = method_id === "cheque" || method_id === "transfer" ? method_id : (methods[method_id] || {}).kind || "other";
    const till_id = kind === "mobile_money" ? String(a.till_id || "") : "";
    if (till_id) accountTillIn[till_id] = r((accountTillIn[till_id] || 0) + amount, 2);
    if (kind === "cash") account_cash_received = r(account_cash_received + amount, 2);
    account_receipts_by_method[method_id] = r((account_receipts_by_method[method_id] || 0) + amount, 2);
    account_receipt_lines.push({ _k: a._k, account_id: a.account_id ? String(a.account_id) : null, method_id: method_id || null,
      kind, till_id: till_id || null, amount, reference: (a.reference || "").trim(), note: (a.note || "").trim(),
      bank: kind === "cheque" || kind === "transfer" ? (a.bank || "").trim() : "" });
  });
  const wrongIn = {}, wrongOut = {};
  const till_transfer_lines = [];
  (shift.till_transfers || []).forEach((t) => {
    const landed = String(t.landed_till_id || ""), meant = String(t.meant_till_id || ""), amount = r(t.amount, 2);
    if (!landed || !meant || landed === meant || !amount) return;
    wrongIn[landed] = r((wrongIn[landed] || 0) + amount, 2);
    wrongOut[meant] = r((wrongOut[meant] || 0) + amount, 2);
    till_transfer_lines.push({ _k: t._k, landed_till_id: landed, meant_till_id: meant, amount,
      reference: (t.reference || "").trim(), note: (t.note || "").trim() });
  });
  const till_lines = (shift.till_ledger || []).map((row) => {
    const till_id = String(row.till_id);
    const till = tills[till_id] || {};
    const attendant_ids = tillAttendantMap[till_id] || [];
    const attendant_id = attendant_ids.length === 1 ? attendant_ids[0] : "";
    const opening_balance = r(row.opening_balance, 2);
    const withdrawal = r(row.withdrawal, 2);
    const declared_mpesa = r(attendant_ids.reduce((a, id) => a + (attendantMpesa[id] || 0), 0) + (tillMpesaDirect[till_id] || 0), 2);
    const wrong_till_in = wrongIn[till_id] || 0, wrong_till_out = wrongOut[till_id] || 0;
    const account_in = accountTillIn[till_id] || 0;
    const mpesa_received = r(declared_mpesa + wrong_till_in - wrong_till_out + account_in, 2);
    const closing_balance = r(opening_balance + mpesa_received - withdrawal, 2);

    const rawActual = row.actual_balance;
    const actual_balance = rawActual !== null && rawActual !== undefined && rawActual !== "" ? r(rawActual, 2) : null;
    const variance = actual_balance !== null ? r(actual_balance - closing_balance, 2) : null;
    let status = null;
    if (variance !== null) {
      status = Math.abs(variance) <= cashTol ? "within" : (Math.abs(variance) <= cashTol * 5 ? "warning" : "critical");
    }

    // Safaricom-confirmed payments into this till during the shift (Daraja C2B) — see calc.py
    const api = (shift.mpesa_api_by_till || {})[till_id];
    const api_received = api ? r(api.amount, 2) : null;
    const api_variance = api_received !== null ? r(mpesa_received - api_received, 2) : null;
    let api_status = null;
    if (api_variance !== null) {
      api_status = Math.abs(api_variance) <= cashTol ? "within" : (Math.abs(api_variance) <= cashTol * 5 ? "warning" : "critical");
    }
    // Withdrawals/charges never come through C2B — implied from the closing reading (see calc.py)
    let implied_withdrawal = null, withdrawal_gap = null;
    if (api_received !== null && actual_balance !== null) {
      implied_withdrawal = r(opening_balance + api_received - actual_balance, 2);
      withdrawal_gap = r(implied_withdrawal - withdrawal, 2);
    }

    return {
      till_id, till_label: till.label || "", till_number: till.till_number || "",
      attendant_id: attendant_id || null, attendant_ids, opening_balance, declared_mpesa, wrong_till_in, wrong_till_out, account_in,
      mpesa_received, withdrawal, closing_balance,
      actual_balance, variance, status,
      api_received, api_count: api ? api.count : null, api_variance, api_status,
      api_window: api ? [api.start, api.end] : null,
      actual_balance_at: row.actual_balance_at || null,
      implied_withdrawal, withdrawal_gap,
    };
  });
  // a till over by about what another is short looks like a wrong-till payment (see calc.py)
  const till_transfer_suggestions = [];
  const shorts = till_lines.filter((l) => l.variance !== null && l.variance < -cashTol);
  till_lines.filter((l) => l.variance !== null && l.variance > cashTol).sort((a, b) => b.variance - a.variance).forEach((over) => {
    let best = null;
    shorts.forEach((sh) => {
      const gap = Math.abs(over.variance + sh.variance);
      if (gap <= WRONG_TILL_TOLERANCE && (!best || gap < Math.abs(over.variance + best.variance))) best = sh;
    });
    if (best) {
      shorts.splice(shorts.indexOf(best), 1);
      till_transfer_suggestions.push({ landed_till_id: over.till_id, meant_till_id: best.till_id, amount: over.variance,
        short: best.variance, leftover: r(over.variance + best.variance, 2) });
    }
  });
  // ----- M-Pesa itemized per attendant (see calc.py): code + amount vs declared, each code checked -----
  const codeLookup = shift.mpesa_code_lookup || {};
  const apiLive = !!shift.mpesa_api_live;
  const mmMethods = new Set(Object.entries(methods).filter(([, m]) => m.kind === "mobile_money").map(([id]) => id));
  const attTills = {};
  Object.entries(tillAttendantMap).forEach(([tid, ids]) => ids.forEach((a) => { (attTills[a] = attTills[a] || []).push(tid); }));
  const codeCount = {};
  (shift.payment_transactions || []).forEach((t) => {
    const c = mpesaCode(t.reference);
    if (c && mmMethods.has(String(t.method_id))) codeCount[c] = (codeCount[c] || 0) + 1;
  });
  const recordedRefs = new Set((shift.till_transfers || []).map((t) => mpesaCode(t.reference)));
  const mpesa_checks = [];
  Object.keys(lumpByGroup).sort().forEach((key) => {
    const [att, mid] = key.split("|");
    const lump = lumpByGroup[key];
    if (!mmMethods.has(mid) || !lump) return;
    const rows = [];
    (shift.payment_transactions || []).forEach((t) => {
      if (String(t.attendant_id || "") !== att || String(t.method_id) !== mid) return;
      const code = mpesaCode(t.reference), amount = r(t.amount, 2), flags = [];
      const info = codeLookup[code] || {};
      const saf = info.safaricom || null;
      if (!code) flags.push("no_code");
      else if (!MPESA_CODE_RE.test(code)) flags.push("bad_format");
      if (code && (codeCount[code] || 0) > 1) flags.push("duplicate");
      if ((info.other_shifts || []).length) flags.push("used_elsewhere");
      if (apiLive && code && MPESA_CODE_RE.test(code)) {
        if (!saf) flags.push("not_on_safaricom");
        else {
          if (Math.abs(r(saf.amount, 2) - amount) > 0.5) flags.push("amount_differs");
          if (saf.till_id && (attTills[att] || []).length && !attTills[att].includes(saf.till_id)) {
            flags.push("other_till");
            if (!recordedRefs.has(code)) {
              till_transfer_suggestions.push({ landed_till_id: saf.till_id, meant_till_id: attTills[att][0], amount: r(saf.amount, 2),
                short: null, leftover: 0, reference: code, source: "safaricom" });
              recordedRefs.add(code); // one suggestion per code
            }
          }
        }
      }
      rows.push({ code, amount, flags, safaricom: saf, other_shifts: info.other_shifts || [] });
    });
    const itemized = r(rows.reduce((x, y) => x + y.amount, 0), 2);
    mpesa_checks.push({ attendant_id: att || null, method_id: mid, declared: lump, itemized, difference: r(lump - itemized, 2),
      count: rows.length, rows, problems: rows.filter((x) => x.flags.length).length });
  });
  // Visa and Shell Card per attendant: date, receipt no., amount + authorization code (Visa) / product (Shell Card)
  const card_checks = [];
  const receiptCount = {};
  (shift.payment_transactions || []).forEach((t) => {
    const rec = String(t.reference || "").trim().toUpperCase();
    if (rec && (methods[String(t.method_id)] || {}).kind === "card") receiptCount[`${t.method_id}|${rec}`] = (receiptCount[`${t.method_id}|${rec}`] || 0) + 1;
  });
  Object.keys(lumpByGroup).sort().forEach((key) => {
    const [att, mid] = key.split("|");
    const lump = lumpByGroup[key];
    const m = methods[mid] || {};
    if (m.kind !== "card" || !lump) return;
    const shell = String(m.name || "").toLowerCase().includes("shell");
    const rows = [];
    (shift.payment_transactions || []).forEach((t) => {
      if (String(t.attendant_id || "") !== att || String(t.method_id) !== mid) return;
      const rec = String(t.reference || "").trim().toUpperCase(), flags = [];
      if (!rec) flags.push("no_receipt");
      else if ((receiptCount[`${mid}|${rec}`] || 0) > 1) flags.push("duplicate");
      if (shell && !t.product) flags.push("no_product");
      if (!shell && !String(t.auth_code || "").trim()) flags.push("no_auth");
      rows.push({ receipt: rec, amount: r(t.amount, 2), flags });
    });
    const itemized = r(rows.reduce((x, y) => x + y.amount, 0), 2);
    card_checks.push({ attendant_id: att || null, method_id: mid, method_name: m.name || "", card: shell ? "shell" : "visa",
      declared: lump, itemized, difference: r(lump - itemized, 2), count: rows.length, rows, problems: rows.filter((x) => x.flags.length).length });
  });
  // a till over with no till short to match may be a customer paying their account into it
  const paired = new Set(till_transfer_suggestions.map((sg) => sg.landed_till_id));
  const account_receipt_hints = till_lines.filter((l) => l.variance !== null && l.variance > cashTol && !paired.has(l.till_id))
    .map((l) => ({ till_id: l.till_id, amount: l.variance }));

  // Expenses = itemized lines (Section E), each optionally "paid out by" an attendant's Cash Out;
  // a Cash Out not yet itemized is carried as "Cash Out — not itemized". Payouts count once, as
  // expenses, and are taken back out of non-cash (see calc.py)
  const attendantNames = Object.fromEntries((config.attendants || []).map((a) => [String(a.id), a.name]));
  const expense_lines = (shift.expenses || []).map((e) => ({
    category: e.category || "Uncategorized", description: e.description || "", voucher_no: e.voucher_no || "",
    amount: r(e.amount, 2), source: "manual", paid_by: e.paid_by ? String(e.paid_by) : null,
  }));
  const cashOutByAtt = {};
  [...payment_lines, ...nonfuel_payment_lines].forEach((line) => {
    if (line.kind === "payout" && line.amount) {
      const att = String(line.attendant_id || "");
      cashOutByAtt[att] = r((cashOutByAtt[att] || 0) + line.amount, 2);
    }
  });
  const itemizedByAtt = {};
  expense_lines.forEach((e) => { if (e.paid_by) itemizedByAtt[e.paid_by] = r((itemizedByAtt[e.paid_by] || 0) + e.amount, 2); });
  const cash_out_checks = [...new Set([...Object.keys(cashOutByAtt), ...Object.keys(itemizedByAtt)])].sort().map((att) => {
    const cash_out = cashOutByAtt[att] || 0;
    const itemized = itemizedByAtt[att] || 0;
    const difference = r(cash_out - itemized, 2);
    if (difference > 0) {
      expense_lines.push({ category: "Cash Out — not itemized", description: attendantNames[att] || "Shop", voucher_no: "",
        amount: difference, source: "cash_out_unitemized", paid_by: att || null });
    }
    return { attendant_id: att || null, attendant_name: attendantNames[att] || "Shop", cash_out, itemized, difference };
  });
  // Invoice itemization: each attendant's credit-sale total vs their invoice lines (see calc.py)
  const linkedMethods = new Set((config.receivable_accounts || []).filter((a) => a.method_id).map((a) => String(a.method_id)));
  const invDeclared = {};
  [...payment_lines, ...nonfuel_payment_lines.filter((l) => l.group === "lube" || l.group === "lpg_accessory")].forEach((line) => {
    if (line.kind === "invoice" && line.amount) {
      const k = `${line.attendant_id || ""}|${line.method_id}`;
      invDeclared[k] = r((invDeclared[k] || 0) + line.amount, 2);
    }
  });
  const invItemized = {};
  (shift.invoice_lines || []).forEach((ln) => {
    if (r(ln.amount, 2)) {
      const k = `${ln.attendant_id || ""}|${ln.method_id || ""}`;
      invItemized[k] = r((invItemized[k] || 0) + r(ln.amount, 2), 2);
    }
  });
  const invoice_checks = [...new Set([...Object.keys(invDeclared), ...Object.keys(invItemized)])].sort().map((k) => {
    const [att, mid] = k.split("|");
    const declared = invDeclared[k] || 0;
    const itemized = invItemized[k] || 0;
    return { attendant_id: att || null, attendant_name: attendantNames[att] || "—", method_id: mid,
      method_name: (methods[mid] || {}).name || "", declared, itemized, difference: r(declared - itemized, 2), optional: linkedMethods.has(mid) };
  });
  non_cash_total = r(non_cash_total - Object.values(cashOutByAtt).reduce((a, v) => a + v, 0), 2);
  const expenses_total = r(expense_lines.reduce((a, e) => a + e.amount, 0), 2);

  const total_sales = r(fuel_sales_total + nonfuel_revenue_total, 2);
  // Expected cash uses the attendants' expected fuel (Vivo, or nozzle meters when automation is
  // down) — same basis as the attendant checks; meter total when no Vivo figure exists (see calc.py)
  const fuel_expected_basis = hasSystem ? r(attendant_lines.reduce((a, l) => a + l.assigned_fuel_sales, 0), 2) : r(fuel_sales_total, 2);
  const expected_cash_basis = hasSystem ? "vivo" : "meters";
  const expected_cash = r(fuel_expected_basis + nonfuel_revenue_total - non_cash_total - expenses_total, 2);
  const actual_cash = r(cash_declared, 2); // sum of "Cash" entries across all attendants
  const cash_variance = r(actual_cash - expected_cash, 2);
  let cash_status = "within";
  if (Math.abs(cash_variance) > cashTol * 5) cash_status = "critical";
  else if (Math.abs(cash_variance) > cashTol) cash_status = "warning";

  return {
    fuel_lines, tank_lines, tank_delivery_lines, nonfuel_delivery_lines, nonfuel_lines, nonfuel_group_lines, nonfuel_payment_lines, store_sale_lines, payment_lines, attendant_lines, payment_itemization, till_lines, till_transfer_lines, till_transfer_suggestions, account_receipt_lines, account_receipt_hints, mpesa_checks, card_checks, expense_lines, cash_out_checks, invoice_checks,
    totals: {
      fuel_sales_total: r(fuel_sales_total, 2),
      fuel_cost_total: r(fuel_cost_total, 2),
      fuel_gross_total: r(fuel_sales_total - fuel_cost_total, 2),
      nonfuel_revenue_total: r(nonfuel_revenue_total, 2),
      store_sales_total,
      nonfuel_revenue_by_group,
      nonfuel_cost_total: r(nonfuel_cost_total, 2),
      nonfuel_gross_total: r(nonfuel_revenue_total - nonfuel_cost_total, 2),
      payments_total: r(payments_total, 2),
      payments_by_method,
      non_cash_total: r(non_cash_total, 2),
      cash_declared: r(cash_declared, 2),
      expenses_total: r(expenses_total, 2),
      total_sales, fuel_expected_basis, expected_cash_basis, expected_cash, actual_cash, cash_variance, cash_status,
      account_receipts_total: r(account_receipt_lines.reduce((x, l) => x + l.amount, 0), 2), account_receipts_by_method,
      // Shell Card top-ups: account payments against the Shell Card (Vivo) account
      topups_total: r(account_receipt_lines.filter((l) => (config.receivable_accounts || []).some((a) => String(a.id) === l.account_id && a.kind === "card" && /shell/i.test(a.name || ""))).reduce((x, l) => x + l.amount, 0), 2),
      account_cash_received, cash_to_bank: r(actual_cash + account_cash_received, 2),
      mpesa_itemize_from: station.mpesa_itemize_from || null,
      mpesa_itemize_required: !!station.mpesa_itemize_from && String(shift.date || "") >= String(station.mpesa_itemize_from),
      card_itemize_from: station.card_itemize_from || null,
      card_itemize_required: !!station.card_itemize_from && String(shift.date || "") >= String(station.card_itemize_from),
      total_tank_variance_l: r(total_tank_variance_l),
      rtt_liters_total, rtt_value_total,
      attendant_lube_total,
      system_expected_total: hasSystem ? r(Object.values(systemExpected).reduce((a, v) => a + v, 0), 2) : null,
      vivo_check: hasSystem && vivoIncludesNonfuel ? (() => {
        const vivo_total = r(Object.values(vivoReported).reduce((a, v) => a + v, 0), 2);
        const viaVivo = r(nonfuel_revenue_total - store_sales_total, 2);
        return { vivo_total, nonfuel_sold: viaVivo, vivo_fuel: r(vivo_total - viaVivo, 2),
          meter_fuel: r(fuel_sales_total, 2), difference: r(vivo_total - viaVivo - fuel_sales_total, 2) };
      })() : null,
      system_vs_meter_variance: hasSystem ? r(Object.values(systemExpected).reduce((a, v) => a + v, 0) - fuel_sales_total, 2) : null,
    },
  };
}
